// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Pen, Type, Trash2, Save, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SignatureModal({ open, onOpenChange, onSuccess }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [textSignature, setTextSignature] = useState('');
  const [signatureType, setSignatureType] = useState('draw');
  const [loading, setLoading] = useState(false);
  const [existingSignature, setExistingSignature] = useState(null);

  useEffect(() => {
    if (open) {
      loadExistingSignature();
    }
  }, [open]);

  useEffect(() => {
    if (open && signatureType === 'draw') {
      initCanvas();
    }
  }, [open, signatureType]);

  const loadExistingSignature = async () => {
    try {
      const response = await axios.get(`${API}/signature/me`);
      if (response.data) {
        setExistingSignature(response.data);
        if (response.data.signature_type === 'text') {
          setTextSignature(response.data.signature_data);
          setSignatureType('text');
        }
      }
    } catch (error) {
      // 404 here just means the user has no signature yet — that's a normal
      // "first time" path. Other errors are surfaced for debugging.
      if (error.response?.status !== 404) {
        console.error('Error loading existing signature:', error);
      }
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let signatureData;
      let type;
      
      if (signatureType === 'draw') {
        const canvas = canvasRef.current;
        signatureData = canvas.toDataURL('image/png');
        type = 'image';
      } else {
        if (!textSignature.trim()) {
          toast.error('Digite sua rubrica');
          setLoading(false);
          return;
        }
        signatureData = textSignature.trim();
        type = 'text';
      }
      
      await axios.post(`${API}/signature`, {
        signature_data: signatureData,
        signature_type: type
      });
      
      toast.success('Rubrica salva com sucesso!');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar rubrica';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingSignature) return;
    
    if (!window.confirm('Deseja excluir sua rubrica?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/signature`);
      toast.success('Rubrica excluída');
      setExistingSignature(null);
      setTextSignature('');
      initCanvas();
    } catch (error) {
      toast.error('Erro ao excluir rubrica');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Criar Minha Rubrica</DialogTitle>
          <DialogDescription>
            Desenhe ou digite sua rubrica para usar na assinatura de documentos
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="draw" className="flex items-center gap-2">
              <Pen className="w-4 h-4" />
              Desenhar
            </TabsTrigger>
            <TabsTrigger value="text" className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              Texto
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="draw" className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-2 bg-white">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <Button variant="outline" onClick={clearCanvas} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Limpar
            </Button>
          </TabsContent>
          
          <TabsContent value="text" className="space-y-4">
            <div className="space-y-2">
              <Label>Digite sua rubrica</Label>
              <Input
                value={textSignature}
                onChange={(e) => setTextSignature(e.target.value)}
                placeholder="Suas iniciais ou nome"
                className="text-xl font-signature h-14 text-center"
                maxLength={30}
              />
            </div>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-6 bg-white text-center">
              <p className="text-sm text-muted-foreground mb-2">Prévia:</p>
              <p className="text-2xl font-signature text-slate-800 italic">
                {textSignature || 'Sua rubrica aqui'}
              </p>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="flex gap-2 sm:gap-2">
          {existingSignature && (
            <Button variant="outline" onClick={handleDelete} disabled={loading} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          )}
          <Button onClick={handleSave} disabled={loading} className="flex-1">
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar Rubrica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
